<div class="entity-config-detail-page container-fluid px-0 py-0 py-lg-1">
  <div class="entity-config-shell d-flex flex-column gap-1 mx-auto">
    <section class="entity-config-top rounded-4 overflow-hidden shadow-sm">
      <div class="entity-config-top-hero px-2 px-md-2 py-2">
        <div class="row g-2 align-items-start flex-nowrap">
          <div class="col">
            <h1 class="entity-page-title text-white fw-bold lh-sm mb-1">Entity Configuration Assignment</h1>
            <p class="entity-page-subtitle text-white mb-0">
              Seamlessly orchestrate and assign entity configurations across your active projects.
            </p>
          </div>
        </div>
      </div>

      <div v-if="entity_types_loading || entity_types_error || blueprint_library.length > 0" class="px-2 px-md-2 pb-2">
        <section class="row entity-config-top-metrics g-3 g-xl-4 align-items-stretch">
          <div class="col-12 col-lg-8 mt-2">
            <div class="card border border-purple border-opacity-50 shadow-sm rounded-4 h-100">
              <div class="card-body p-2 p-lg-3 d-flex flex-column gap-3">
                <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-2">
                  <div class="d-flex align-items-center gap-2">
                    <div class="entity-icon-surface rounded-4 d-inline-flex align-items-center justify-content-center p-2 flex-shrink-0 px-3">
                      <i class="bi bi-clipboard-check fs-2"></i>
                    </div>
                    <div>
                      <p class="fw-bold mb-0 text-x">{{ featured_blueprint.title }}</p>
                      <div class="d-flex flex-wrap gap-2 text-secondary text-xs">
                        <span>{{ featured_blueprint.version }}</span>
                        <span>&bull;</span>
                        <span>{{ featured_blueprint.modified }}</span>
                      </div>
                    </div>
                  </div>
                  <span
                    class="badge rounded-pill px-3 py-1 text-uppercase fw-semibold small"
                    :class="featured_blueprint.status_class === 'success' ? 'text-success bg-success-subtle' : 'text-secondary bg-light'"
                  >
                    {{ featured_blueprint.status }}
                  </span>
                </div>

                <p class="text-xs lh-base text-secondary mb-0">
                  {{ featured_blueprint.description }}
                </p>

                <div class="row g-2 align-items-start">
                  <div class="col-12 col-sm-7">
                    <div class="text-uppercase fw-bold text-secondary text-xs mb-2 tracking-wide">
                      Subsystem Binding
                    </div>
                    <div class="d-flex flex-wrap gap-2">
                      <span
                        v-for="binding in featured_blueprint.subsystem_bindings"
                        :key="binding"
                        class="d-inline-flex align-items-center border rounded-3 bg-white px-2 py-1 text-xs fw-semibold"
                      >
                        {{ binding }}
                      </span>
                    </div>
                    <div v-if="bindings_loading" class="d-flex align-items-center gap-2 text-secondary small mt-2">
                      <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                      <span>Loading bindings...</span>
                    </div>
                    <div v-if="bindings_error" class="text-danger small mt-2">
                      {{ bindings_error }}
                    </div>
                    <div v-if="!bindings_loading && !bindings_error && !featured_blueprint.subsystem_bindings?.length"
                      class="text-secondary text-xs mt-1">
                      No subsystem bindings configured.
                    </div>
                  </div>

                  <div class="col-12 col-sm-5">
                    <div class="text-uppercase fw-bold text-secondary text-xs mb-2 tracking-wide">
                      Active Projects
                    </div>
                    <div class="d-flex flex-wrap align-items-center gap-2">
                      <span
                        v-for="project in featured_active_projects_display"
                        :key="project.key"
                        class="d-inline-flex align-items-center justify-content-center rounded-circle fw-semibold small lh-1 p-2"
                        :class="project.is_more ? 'bg-light text-secondary' : 'bg-primary-subtle text-primary cursor-pointer'"
                        :title="project.name || ''"
                        role="button"
                        tabindex="0"
                        @click="project.is_more ? go_to_project_management() : go_to_project_management(project.id)"
                        @keydown.enter.prevent="project.is_more ? go_to_project_management() : go_to_project_management(project.id)"
                      >
                        {{ project.label }}
                      </span>
                    </div>
                  </div>
                </div>

                <hr class="my-0 text-secondary opacity-10">

                <div class="d-flex justify-content-end mt-3">
                  <button
                    type="button"
                    class="btn btn-primary-dark btn-sm text-xs d-inline-flex align-items-center gap-2 rounded-3 px-3 py-2 shadow"
                    data-bs-toggle="modal"
                    data-bs-target="#assign_project"
                    @click="open_assign_modal(featured_blueprint)"
                  >
                    Assign Project
                    <i class="bi bi-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12 col-lg-4 mt-2">
            <div
              class="card border border-purple border-opacity-50 shadow-sm rounded-4 h-100 cursor-pointer"
              role="button"
              tabindex="0"
              @click="feature_secondary_blueprint"
              @keydown.enter.prevent="feature_secondary_blueprint"
              @keydown.space.prevent="feature_secondary_blueprint"
            >
              <div class="card-body p-3 p-lg-4 d-flex flex-column">
                <div class="d-flex justify-content-end mb-3">
                  <span
                    class="badge text-uppercase small"
                    :class="secondary_blueprint.status_class === 'success' ? 'text-success bg-success-subtle' : 'text-secondary bg-light'"
                  >
                    {{ secondary_blueprint.status }}
                  </span>
                </div>

                <div class="d-flex justify-content-center mb-3">
                  <div class="entity-icon-surface rounded-3 d-inline-flex align-items-center justify-content-center p-2 px-3">
                    <i class="bi bi-bank fs-2"></i>
                  </div>
                </div>

                <p class="fw-bold mb-1 text-x">{{ secondary_blueprint.title }}</p>
                <p class="text-secondary text-x mb-4">{{ secondary_blueprint.description }}</p>

                <div class="mt-auto">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="text-muted fw-semibold text-xs">Binding Status</span>
                    <span class="fw-semibold text-danger text-xs">{{ secondary_blueprint.bound_count }}/{{ secondary_blueprint.total_count }} Partial</span>
                  </div>
                  <div class="progress entity-health-progress mb-4">
                    <div
                      class="progress-bar entity-health-bar"
                      role="progressbar"
                      :style="{ width: (secondary_blueprint.bound_count / secondary_blueprint.total_count * 100) + '%' }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>

    <!-- Empty state -->
    <div v-if="!entity_types_loading && !entity_types_error && blueprint_library.length === 0"
      class="card border border-purple border-opacity-50 shadow-lg rounded-4 mt-2">
      <div class="card-body d-flex flex-column align-items-center justify-content-center gap-3 py-3 text-center">
        <div class="bg-light rounded-4 d-inline-flex align-items-center justify-content-center p-2 px-3">
          <i class="bi bi-diagram-3 fs-2 text-secondary"></i>
        </div>
        <div>
          <p class="fw-bold mb-1 text-x">No entity configurations yet</p>
          <p class="text-secondary text-xs mb-0">Create your first entity type to start configuring<br>and assigning projects.</p>
        </div>
        <button
          type="button"
          class="btn btn-primary-dark text-xs d-inline-flex align-items-center gap-2 rounded-3 px-3 py-2 shadow-sm" @click="redirect_to_create_entity">
          <i class="bi bi-plus-circle"></i>
          <span class="fw-semibold"> Create entity type</span>
        </button>
      </div>
    </div>

    <!-- Main content -->
    <div v-if="entity_types_loading || entity_types_error || blueprint_library.length > 0" class="d-flex flex-column gap-2">
      <section class="entity-inventory-panel rounded-4 border shadow-sm p-2 mt-3">
        <div class="row g-2 align-items-start align-items-lg-center mb-3">
          <div class="col-12 col-lg">
            <div class="d-flex align-items-center gap-3">
              <div class="entity-section-icon rounded-3 d-inline-flex align-items-center justify-content-center">
                <i class="bi bi-grid-3x3-gap-fill"></i>
              </div>
              <div class="entity-section-title fw-bold fs-6">Extended Blueprint Library</div>
            </div>
          </div>

          <div class="col-12 col-lg-auto">
            <div class="d-grid d-sm-flex align-items-stretch gap-2">
              <div class="d-inline-flex align-items-center gap-2">
                <i class="bi bi-funnel-fill"></i>
                <select
                  v-model="status_filter"
                  class="form-select form-select-sm rounded-small"
                  aria-label="Filter blueprint library"
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div class="d-inline-flex align-items-center gap-2">
                <i class="bi bi-sort-down"></i>
                <select
                  v-model="sort_option"
                  class="form-select form-select-sm rounded-small"
                  aria-label="Sort blueprint library"
                >
                  <option value="updated_desc">Newest Updated</option>
                  <option value="updated_asc">Oldest Updated</option>
                  <option value="name_asc">Name A-Z</option>
                  <option value="name_desc">Name Z-A</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div v-if="entity_types_loading" class="d-flex align-items-center gap-2 text-secondary small">
          <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
          <span>Loading entity types...</span>
        </div>

        <div v-if="entity_types_error" class="alert alert-warning text-xs py-2 mb-0" role="alert">
          {{ entity_types_error }}
        </div>

        <div class="entity-table-head secondary-color d-none d-lg-block rounded-3 mb-2 px-3 py-2 text-uppercase tracking-wide">
          <div class="row g-3 align-items-center">
            <div class="col-lg-5">Entity Type Name</div>
            <div class="col-lg-2">Status</div>
            <div class="col-lg-2">Version</div>
            <div class="col-lg-2">Summary of Bindings</div>
            <div class="col-lg-1"></div>
          </div>
        </div>

        <div class="d-flex flex-column gap-2">
          <article
            v-for="(item, index) in filtered_blueprint_library"
            :key="item.id || item.title"
            class="entity-row-card bg-white rounded-4 border shadow-sm p-3"
            :class="[
              item.disabled ? 'opacity-50' : 'cursor-pointer',
              String(item.id) === String(selected_blueprint_id) ? 'global-overview-selected-card' : ''
            ]"
            role="button"
            :tabindex="item.disabled ? -1 : 0"
            :aria-disabled="item.disabled ? 'true' : 'false'"
            @click="select_blueprint(item)"
            @keydown.enter.prevent="select_blueprint(item)"
            @keydown.space.prevent="select_blueprint(item)"
          >
            <div class="entity-row-layout row g-3 align-items-center">
              <div class="entity-name-block col-12 col-lg-5 d-flex align-items-start align-items-sm-center gap-3">
                <div
                  class="entity-type-icon entity-icon-surface rounded-3 d-inline-flex align-items-center justify-content-center"
                >
                  <i class="bi bi-shop"></i>
                </div>
                <div>
                  <div class="entity-name fw-bold">{{ item.title }}</div>
                  <div class="entity-updated text-secondary small">{{ item.modified }}</div>
                </div>
              </div>

              <div class="entity-status-block col-12 col-sm-4 col-lg-2">
                <span
                  class="entity-status-pill d-inline-flex align-items-center justify-content-center fw-bold text-uppercase"
                  :class="item.status_class === 'success' ? 'entity-status-active' : 'entity-status-review'"
                >
                  {{ item.status }}
                </span>
              </div>

              <div class="entity-version col-12 col-sm-4 col-lg-2 fw-bold">{{ item.version }}</div>

              <div class="entity-binding-block col-12 col-sm-8 col-lg-2 d-flex flex-wrap align-items-start align-items-sm-center gap-2">
                <div class="entity-binding-text text-secondary small d-inline-flex align-items-center">
                  <span class="badge bg-light text-secondary rounded-3 px-2 py-1 fw-semibold">
                    {{ item.binding_count || 0 }}
                  </span>
                  <span class="ms-1">
                    {{ (item.binding_count || 0) === 1 ? 'binding' : 'bindings' }}
                  </span>
                </div>
              </div>

              <div class="col-lg-1 d-none d-lg-flex justify-content-end">
                <button
                  type="button"
                  class="entity-row-arrow rounded-circle d-inline-flex align-items-center justify-content-center"
                  tabindex="-1"
                >
                </button>
              </div>
            </div>

            <div v-if="item.disabled" class="text-secondary text-xxs fw-semibold mt-2 ps-1">
              Configure first
            </div>
          </article>
        </div>
      </section>

    </div>
  </div>
</div>

<!-- Assign project to entity config modal -->
<teleport to="body">
  <div class="modal fade" id="assign_project" tabindex="-1" aria-labelledby="assign_project_label" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
        <div class="modal-header border-bottom-0 cute-header shadow-lg d-flex align-items-start justify-content-between">
          <div>
            <p class="modal-title text-white text-x mb-0" id="assign_project_label">
              Assign project <span v-if="assign_blueprint?.title" class="fw-normal">— {{ assign_blueprint.title }}</span>
            </p>
            <div class="text-white-50 text-xs">
              Select projects to link to this entity
            </div>
          </div>
          <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body mt-3">
          <div v-if="companies_loading || assign_bindings_loading" class="d-flex align-items-center gap-2 text-secondary text-xs py-2">
            <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
            <span>Loading projects...</span>
          </div>

          <div v-else class="d-flex flex-column gap-2">
            <div
              v-for="project in companies"
              :key="project.id"
              class="border rounded-3 p-2 d-flex align-items-center justify-content-between gap-2 shadow-sm"
            >
              <div class="d-flex align-items-center gap-2 overflow-hidden">
                <div
                  class="rounded-circle bg-primary-subtle text-primary d-inline-flex align-items-center justify-content-center fw-bold flex-shrink-0 assign-project-avatar"
                >
                  <span class="text-xxs">{{ project_short_label(project) }}</span>
                </div>
                <div class="overflow-hidden">
                  <div class="fw-semibold text-xs text-body-emphasis text-truncate">
                    {{ project_display_name(project) || `Project ${project.id}` }}
                  </div>
                </div>
              </div>

              <div class="form-check form-switch m-0">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :checked="is_project_selected(project.id)"
                  :disabled="is_project_toggle_loading(project.id)"
                  @change="on_project_toggle(project, $event)"
                />
              </div>
            </div>

            <div v-if="!companies.length" class="text-center text-secondary text-xs py-4">
              No projects found.
            </div>
          </div>

          <div class="text-secondary text-xs mt-3">
            {{ selected_projects_count }} project{{ selected_projects_count === 1 ? '' : 's' }} assigned
          </div>
        </div>
      </div>
    </div>
  </div>
</teleport>
